import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, SlideOutComp, TimeSeriesType } from '@moderepo/mode-app-components';
import { userAppDataContext, fetchHomeKVStore, selectHomeKVStore } from '@moderepo/mode-data-state-user-app';
import { fetchAgentProjectById, ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { Agent } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { RouteHelper } from '../routes/RouteHelper';
import { DashboardWidgetSettings, DashboardWidgetType, HOME_DASHBOARDS_KV_KEY, isCollectionDashboardWidgetDataSource,
    isDataDashboardWidgetSettings, isEntityDashboardWidgetDataSource, isHomeDashboardsKeyValuePair, isProjectDashboardsSettings,
    isSimpleSeriesDashboardWidgetDataSource, isWidgetGroupWidgetSettings } from './dashboardTypes';
import { DashboardWidgetContainer } from './DashboardWidgetContainer';
import { DashboardLogicApiCallResponse, removeHomeDashboardWidget, removeProjectDashboardWidget } from './dashboardLogic';
import { useSelectEntity } from '../../utils/dataStateHooks';
import { getMetricsDefinitionMetricsId } from '../entities';
import { useSelectTranslatedEntityClasses } from '../entities/utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface DashboardWidgetPreviewContainerProps {
    readonly projectId: number;
    readonly dashboardId: number;

    // selectedWidget can be either a widget type of a widgetId. If the preview panel is used for creating a new widget
    // then it needs to be a widget type. If the preview panel is used for showing a widget info then it need to be a widgetId
    readonly selectedWidget: DashboardWidgetType | number;

    // The ID of the widget, type WidgetGroup, to place the new widget into. This is OPTIONAL and only for creating new widget.
    readonly widgetGroupId?: number | undefined;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}


/**
 * This Component is used for displaying a DashboardWidget in the preview panel for preview
 */
export const DashboardWidgetPreviewContainer: React.FC<DashboardWidgetPreviewContainerProps> = (props: DashboardWidgetPreviewContainerProps) => {
    const navigate = useNavigate();
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const { trans } = useRitzTranslation(project);
    const routeHelper = RouteHelper.getInstance(navigate);
    const classes = useStyle();


    // Check the props.selectedWidget to see if it is a number or widget type. If it is a number, we can assume it is a widgetId
    const selectedWidgetId = typeof props.selectedWidget === 'number' ? props.selectedWidget : undefined;
    const isNewWidget = !selectedWidgetId;


    // Select all the dashboards for the Home/Project from the data state
    const dashboards = useMemo(() => {
        if (props.routeContext?.homeId) {
            // Get dashboards settings from Home KV
            const dashboardKV = selectHomeKVStore(appDataState, props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY);
            if (isHomeDashboardsKeyValuePair(dashboardKV)) {
                return dashboardKV.value;
            }
        } else if (project && isProjectDashboardsSettings(project.supportToolSettings.dashboardSettings)) {
            // Get dashboards settings from Project settings
            return project.supportToolSettings.dashboardSettings.dashboards;
        }

        return undefined;
    }, [appDataState, project, props.routeContext]);


    // Select the dashboard object that this Widget Group belonged to
    const selectedDashboard = useMemo(() => {
        if (dashboards) {
            return dashboards.find((dashboard) => {
                return dashboard.id === props.dashboardId;
            });
        }
        return undefined;
    }, [dashboards, props.dashboardId]);


    // If there is a selectedWidgetId, we need to get the widget settings object for that selectedWidgetId from the home Dashboard data
    const selectedWidget = useMemo(() => {
        if (selectedWidgetId && selectedDashboard) {
            return selectedDashboard.widgets?.find((widget) => {
                return widget.id === selectedWidgetId;
            });
        }

        return undefined;
    }, [selectedDashboard, selectedWidgetId]);


    // Select all entity classes. We might need it if the data source is Entity
    // Select the entity instance and metricsDefinition object if the widget's data source is ENTITY
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const selectedEntity = useSelectEntity(
        props.projectId,
        isDataDashboardWidgetSettings(selectedWidget) && isEntityDashboardWidgetDataSource(selectedWidget.dataSource)
            ? selectedWidget.dataSource.entityId : undefined,
        false,
    );
    const selectedMetricsDefinition = useMemo(() => {
        if (
            isDataDashboardWidgetSettings(selectedWidget)
            && isEntityDashboardWidgetDataSource(selectedWidget.dataSource) && selectedEntity && entityClasses
        ) {
            const { dataSource } = selectedWidget;

            // Find the entityClass that the selectedEntity belonged to
            const entityClass = entityClasses.find((entClass) => {
                return entClass.entityClass === selectedEntity.entityClass;
            });

            if (entityClass) {
                return entityClass.metricsDefinitions?.find((definition) => {
                    return definition.metricsDefinitionId === dataSource.metricsDefinitionId;
                });
            }
        }
        return undefined;
    }, [entityClasses, selectedEntity, selectedWidget]);


    /**
     * This useEffect is used for fetching the Home KV store which is used for storing the provided homeId's dashboard. We need to load
     * the home Dashboard in order to get the widget settings for the selected widget
     */
    useEffect(() => {
        // This container only need the dashboard data for the "View Historical Data" button therefore it DOES NOT need the latest
        // dashboard data. If we already have data from cache then we can use that data without fetching new one
        if (dashboards) {
            return;
        }

        if (props.routeContext?.homeId) {
            appDataDispatch(fetchHomeKVStore(props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY), true, true).catch(() => {
                // ignore error because appDataDispatch already handled the error
            });
        } else {
            dataDispatch(fetchAgentProjectById(loggedInAgent.id, props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appDataDispatch, dashboards, dataDispatch, loggedInAgent.id, props.projectId, props.routeContext, selectedDashboard]);


    /**
     * Unselect the selected widget by closing the slide-out panel
     */
    const hidePreview = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * On 'View Historical Data', take the user to the TSDB historical data page
     */
    const onViewHistoricalData = useCallback(() => {
        if (selectedWidget && isDataDashboardWidgetSettings(selectedWidget)) {
            if (isSimpleSeriesDashboardWidgetDataSource(selectedWidget.dataSource)) {
                routeHelper.gotoHomeHistoricalDataPage({
                    projectId        : props.projectId,
                    userId           : props.routeContext?.userId,
                    homeId           : selectedWidget.dataSource.homeId,
                    tsdbSmartModuleId: selectedWidget.dataSource.smartModuleId,
                    dataId           : selectedWidget.dataSource.seriesIds.join(','),
                    timeSeriesType   : TimeSeriesType.SIMPLE,
                });
                uiDispatch(hideSlideOutPanel());
            } else if (isCollectionDashboardWidgetDataSource(selectedWidget.dataSource)) {
                routeHelper.gotoHomeHistoricalDataPage({
                    projectId        : props.projectId,
                    userId           : props.routeContext?.userId,
                    homeId           : selectedWidget.dataSource.homeId,
                    tsdbSmartModuleId: selectedWidget.dataSource.smartModuleId,
                    dataId           : selectedWidget.dataSource.collectionId,
                    timeSeriesType   : TimeSeriesType.COLLECTION,
                });
                uiDispatch(hideSlideOutPanel());
            } else if (isEntityDashboardWidgetDataSource(selectedWidget.dataSource) && selectedEntity && selectedMetricsDefinition) {
                routeHelper.gotoHomeHistoricalDataPage({
                    projectId        : props.projectId,
                    userId           : props.routeContext?.userId,
                    homeId           : selectedWidget.dataSource.homeId,
                    tsdbSmartModuleId: selectedMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                    dataId           : getMetricsDefinitionMetricsId(selectedEntity, selectedMetricsDefinition.metricsDefinitionId),
                    timeSeriesType   : TimeSeriesType.COLLECTION,
                    metricsDefinition: {
                        entityClass        : selectedEntity.entityClass,
                        metricsDefinitionId: selectedMetricsDefinition.metricsDefinitionId,
                    },
                });
                uiDispatch(hideSlideOutPanel());
            }
        }
    }, [selectedWidget, selectedEntity, selectedMetricsDefinition, routeHelper, props.projectId, props.routeContext, uiDispatch]);


    /**
     * On create new widget canceled event from DashboardWidget we need to hide the slide-out panel
     */
    const onNewWidgetCanceled = useCallback(() => {
        hidePreview();
    }, [hidePreview]);



    /**
     * On create new widget success, we will close the slide-out panel
     */
    const onNewWidgetCreated = useCallback(() => {
        hidePreview();
    }, [hidePreview]);



    /**
     * use useMemo to create the the newWidgetHandler so that this doesn't get created on every re-render. All this function does is return an
     * object with 2 callback functions.newWidgetHandler
     */
    const newWidgetHandler = useMemo(() => {
        return {
            onNewWidgetCreated,
            onNewWidgetCanceled,
        };
    }, [onNewWidgetCanceled, onNewWidgetCreated]);


    /**
     * On Remove widget button clicked. We will confirm before removing the widget from the dashboard.
     */
    const onRemoveWidgetActionClicked = useCallback((widgetToRemove: DashboardWidgetSettings, errorMessage?: string | undefined) => {

        // If this widget is a WidgetGroup, make sure there are no widget in this group before we allow the user to delete this
        if (isWidgetGroupWidgetSettings(widgetToRemove) && selectedDashboard && selectedDashboard.widgets) {
            // Find a widget inside the widgetToBeRemoved
            const innerWidget = selectedDashboard.widgets.find((widget) => {
                return widget.widgetGroupId === widgetToRemove.id;
            });

            // Found a widget therefore show error and cancel delete
            if (innerWidget) {
                uiDispatch(showErrorNotification('notif.delete_widget_group_failed'));
                return;
            }
        }

        // Confirm before removing widget
        uiDispatch(showDialog({
            type          : DialogType.INPUT,
            title         : 'dialog.generic_confirm_delete.title',
            titleData     : { object_term: capitalizeAll(trans('label.dashboard_widget')) },
            message       : 'dialog.generic_confirm_delete.message',
            messageData   : { object_name: widgetToRemove.title, object_term: 'label.dashboard_widget' },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : capitalizeAll(trans('label.name')),
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'label.dashboard_widget',
                    object_field: 'label.name',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {

                    const userResponse = response.input ? response.input.trim() : undefined;

                    if (!userResponse) {
                        // User input is not provided
                        onRemoveWidgetActionClicked(widgetToRemove, 'error.input.generic.empty');
                    } else if (userResponse !== widgetToRemove.title) {
                        // user input does not match dashboardWidgetTitile
                        onRemoveWidgetActionClicked(widgetToRemove, 'error.input.generic.invalid');

                    } else {
                        const onCompleted = (result: DashboardLogicApiCallResponse) => {
                            if (result.success) {
                                uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                                    term: trans('label.dashboard_widget'),
                                }));

                                // The widget is deleted so we should hide the preview panel
                                hidePreview();
                            } else if (result.errorMessageKey) {
                                uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
                            }
                        };

                        if (props.routeContext?.homeId) {
                            removeHomeDashboardWidget(appDataState, appDataDispatch, props.routeContext.homeId, props.dashboardId, widgetToRemove.id)
                                .then(onCompleted);
                        } else {
                            removeProjectDashboardWidget(
                                dataState, dataDispatch, props.projectId, loggedInAgent.id, props.dashboardId, widgetToRemove.id)
                                .then(onCompleted);
                        }
                    }
                }
            },
        }));
    }, [selectedDashboard, uiDispatch, props.routeContext, props.dashboardId, props.projectId, trans, hidePreview, appDataState, appDataDispatch,
        dataState, dataDispatch, loggedInAgent.id]);


    /**
     * Create an Array of primary actions for the dashboard widget
     */
    const primaryActions = useMemo(() => {
        if (selectedWidget && isDataDashboardWidgetSettings(selectedWidget)) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('label.view_historical_data')),
                onClick: () => {
                    onViewHistoricalData();
                },
            }];
            return actions;
        }

        return undefined;
    }, [selectedWidget, trans, onViewHistoricalData]);


    /**
     * Create secondary actions for the dashboard widget
     */
    const secondaryActions = useMemo(() => {
        if (selectedWidget) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.remove_dashboard_widget')),
                onClick  : () => {
                    onRemoveWidgetActionClicked(selectedWidget);
                },
            }];
            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, onRemoveWidgetActionClicked, selectedWidget, trans]);



    /**
     * Create the content for the slide-out panel. The content will just going to be a DashboardWidget
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <DashboardWidgetContainer
                className="transparent"
                projectId={projectId}
                dashboardId={props.dashboardId}
                selectedWidget={props.selectedWidget}
                widgetGroupId={props.widgetGroupId}
                isSettingsView          // The widget is being displayed in a Slide-out panel so we will display it in Settings view
                newWidgetHandler={
                    isNewWidget ? newWidgetHandler : undefined
                }
                routeContext={props.routeContext}
            />
        );
    }, [isNewWidget, newWidgetHandler, projectId, props.dashboardId, props.routeContext, props.selectedWidget, props.widgetGroupId]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewWidget}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
